import { AppSettings } from '@/AppSettings';
import { crfReadOnly } from '@/utils/CrfFormUtils';
import { CommentsAndIssuesEventForm } from '@/api/dtos';
import SourceDocuments from '@/components/documents/SourceDocuments';
import { Box, Typography } from '@mui/material';
import { ValidateOn } from '@ngt/forms-core';
import {
    AuthenticatedUserContext,
    CollaboratingGroupContext,
    CrfForm,
    DatePicker,
    FormBreadcrumbs,
    FormDefinitionContext,
    GroupedFields,
    GroupedTableArrayField,
    IGroupedTableArrayFieldColumn,
    Input,
    InstitutionContext,
    MasterGroupContext,
    PageLayout,
    PatientContext,
    RouteLoading,
    Text,
} from '@ngt/opms';
import { Permission, usePermissionsByIds } from '@ngt/opms-bctapi';
import { DateTime } from 'luxon';
import { FC, useCallback, useContext, useMemo } from 'react';

const CommentsAndIssuesFields: FC = () => {
    const { data: authenticatedUser } = useContext(AuthenticatedUserContext);

    const columns = useMemo<Array<IGroupedTableArrayFieldColumn<CommentsAndIssuesEventForm>>>(() => [
        {
            propertyPath: 'date',
            content: <Input component={DatePicker} />,
        },
        {
            propertyPath: 'user',
            content: <Input component={Text} />,
        },
        {
            propertyPath: 'comments',
            content: <Input component={Text} multiline minRows={4} variant="outlined" />,
        },
    ], []);

    const createNewItem = useCallback(() => new CommentsAndIssuesEventForm({
        date: DateTime.now().toISODate() ?? undefined,
        user: authenticatedUser?.displayName?.trim() || authenticatedUser?.userName?.trim(),
    }), [authenticatedUser?.displayName, authenticatedUser?.userName]);

    return (
        <>
            <GroupedFields>
                <Typography variant="h2" sx={{ px: 2, pt: 3, pb: 1 }}>
                    Comments and Issues
                </Typography>
                <GroupedTableArrayField<CommentsAndIssuesEventForm>
                    entityName="Comment / Issue"
                    propertyPath="commentsAndIssuesEvents"
                    columns={columns}
                    createNewItem={createNewItem}
                    AddButtonProps={{ sx: { mb: 2 } }}
                />
            </GroupedFields>
            <SourceDocuments />
        </>
    );
};

const CommentsAndIssuesForm: FC = () => {
    const { data: masterGroup } = useContext(MasterGroupContext);
    const { data: collaboratingGroup } = useContext(CollaboratingGroupContext);
    const { data: institution } = useContext(InstitutionContext);
    const { data: patient } = useContext(PatientContext);
    const { data: formDefinition } = useContext(FormDefinitionContext);

    const {
        permissions: [canUpdateCrf],
        loading: permissionsLoading,
    } = usePermissionsByIds(
        [Permission.OpmsCrfUpdate],
        masterGroup?.id,
        collaboratingGroup?.id,
        institution?.id,
        patient?.id
    );

    if (permissionsLoading) {
        return <RouteLoading />;
    }

    return (
        <PageLayout breadcrumbs={<FormBreadcrumbs />}>
            <CrfForm
                validateOn={ValidateOn.onChange}
                readOnly={crfReadOnly(canUpdateCrf)}
                debug={AppSettings.debug}
                AdditionalContent={{
                    beforeChildren: formDefinition?.instructions ?
                        <Box
                            sx={{
                                p: 2,
                            }}
                        >
                            {formDefinition?.instructions}
                        </Box> :
                        undefined
                }}
            >
                <CommentsAndIssuesFields />
            </CrfForm>
        </PageLayout>
    );
};

export default CommentsAndIssuesForm;
